import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { nomineeAPI } from '../services/api';

const Container = styled.div`
  min-height: 100vh;
  padding: 2rem;
`;

const Card = styled.div`
  background: white;
  border-radius: 20px;
  padding: 2rem;
  max-width: 800px;
  margin: 0 auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  text-align: center;
  color: #1f2937;
  margin-bottom: 2rem;
`;

const BackButton = styled.button`
  background: #667eea;
  color: white;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 1rem;
  transition: all 0.2s ease;
  
  &:hover {
    background: #5a67d8;
    transform: translateY(-1px);
  }
`;

const AddNomineeSection = styled.div`
  background: #f9fafb;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  border: 2px dashed #d1d5db;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #d1d5db;
  border-radius: 8px;
  margin-bottom: 1rem;
  fontSize: 1rem;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #d1d5db;
  border-radius: 8px;
  margin-bottom: 1rem;
  fontSize: 1rem;
  background: white;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #d1d5db;
  border-radius: 8px;
  margin-bottom: 1rem;
  fontSize: 1rem;
  min-height: 80px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const Button = styled.button`
  background: #667eea;
  color: white;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  fontWeight: 600;
  transition: all 0.2s ease;
  
  &:hover {
    background: #5a67d8;
    transform: translateY(-1px);
  }
  
  &:disabled {
    background: #cbd5e0;
    color: #a0aec0;
    cursor: not-allowed;
    transform: none;
  }
`;

const NomineesList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const NomineeCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
`;

const NomineeName = styled.h3`
  color: #1f2937;
  margin: 0 0 0.5rem 0;
  fontSize: 1.2rem;
`;

const NomineeDescription = styled.p`
  color: #6b7280;
  margin: 0 0 1rem 0;
  fontSize: 0.9rem;
`;

const NomineeInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  fontSize: 0.8rem;
  color: #9ca3af;
  margin-bottom: 1rem;
`;

const NomineeStats = styled.div`
  display: flex;
  gap: 1rem;
  fontSize: 0.8rem;
  color: #6b7280;
  
  span {
    background: #f3f4f6;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const SmallButton = styled.button`
  background: white;
  color: #667eea;
  border: 2px solid #667eea;
  padding: 0.4rem 0.8rem;
  border-radius: 6px;
  cursor: pointer;
  fontSize: 0.8rem;
  fontWeight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: #667eea;
    color: white;
    transform: translateY(-1px);
  }
`;

const DeleteButton = styled(SmallButton)`
  background: white;
  color: #dc2626;
  border-color: #dc2626;
  
  &:hover {
    background: #dc2626;
    color: white;
  }
`;

interface Nominee {
  id: number;
  room_id: number;
  user_id: number;
  name: string;
  description?: string;
  creator_username: string;
  creator_name: string;
  photo_count: number;
  nickname_count: number;
  created_at: string;
}

interface RoomMember {
  id: number;
  username: string;
  name: string;
  email: string;
  joined_at: string;
  is_admin: boolean;
}

const NomineeManagement: React.FC = () => {
  const [nominees, setNominees] = useState<Nominee[]>([]);
  const [roomMembers, setRoomMembers] = useState<RoomMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [newNominee, setNewNominee] = useState({
    name: '',
    description: '',
    user_id: ''
  });
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const roomId = searchParams.get('room_id');

  const loadNominees = useCallback(async () => {
    if (!roomId) return;
    try {
      setLoading(true);
      const data = await nomineeAPI.getRoomNominees(parseInt(roomId));
      setNominees(data);
    } catch (error) {
      console.error('Error loading nominees:', error);
    } finally {
      setLoading(false);
    }
  }, [roomId]);

  const loadRoomMembers = useCallback(async () => {
    if (!roomId) return;
    try {
      const response = await fetch(`https://api.eventoshub.suils.es/api/nominees/room-members?room_id=${roomId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'X-Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const result = await response.json();
      if (result.success) {
        setRoomMembers(result.data);
      }
    } catch (error) {
      console.error('Error loading room members:', error);
    }
  }, [roomId]);

  useEffect(() => {
    if (roomId) {
      loadNominees();
      loadRoomMembers();
    }
  }, [roomId, loadNominees, loadRoomMembers]);

  const handleCreateNominee = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newNominee.name.trim() || !newNominee.user_id || !roomId) return;

    try {
      await nomineeAPI.createNominee({
        room_id: parseInt(roomId),
        user_id: parseInt(newNominee.user_id),
        name: newNominee.name.trim(),
        description: newNominee.description.trim() || undefined
      });
      
      setNewNominee({ name: '', description: '', user_id: '' });
      loadNominees();
    } catch (error) {
      console.error('Error creating nominee:', error);
      alert('Error al crear nominado. Inténtalo de nuevo.');
    }
  };

  const handleDeleteNominee = async (nomineeId: number) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este nominado?')) return;

    try {
      await nomineeAPI.deleteNominee(nomineeId);
      loadNominees();
    } catch (error) {
      console.error('Error deleting nominee:', error);
      alert('Error al eliminar nominado.');
    }
  };

  if (!roomId) {
    return (
      <Container>
        <Card>
          <Title>Error</Title>
          <p>No se especificó la sala. Por favor regresa al dashboard.</p>
          <BackButton onClick={() => navigate('/dashboard')}>
            Volver al Dashboard
          </BackButton>
        </Card>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container>
        <Card>
          <Title>Cargando nominados...</Title>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <BackButton onClick={() => navigate(`/dashboard?room_id=${roomId}`)}>
          ← Volver al Dashboard
        </BackButton>
        
        <Title>👥 Gestionar Nominados</Title>
        
        <AddNomineeSection>
          <h3 style={{ color: '#374151', marginBottom: '1rem' }}>➕ Agregar Nuevo Nominado</h3>
          <form onSubmit={handleCreateNominee}>
            <Input
              type="text"
              placeholder="Nombre del nominado"
              value={newNominee.name}
              onChange={(e) => setNewNominee({ ...newNominee, name: e.target.value })}
              required
            />
            <Select
              value={newNominee.user_id}
              onChange={(e) => setNewNominee({ ...newNominee, user_id: e.target.value })}
              required
            >
              <option value="">Seleccionar usuario a nominar</option>
              {roomMembers.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.name} (@{member.username})
                </option>
              ))}
            </Select>
            <TextArea
              placeholder="Descripción (opcional)"
              value={newNominee.description}
              onChange={(e) => setNewNominee({ ...newNominee, description: e.target.value })}
            />
            <Button type="submit" disabled={!newNominee.name.trim() || !newNominee.user_id}>
              Crear Nominado
            </Button>
          </form>
        </AddNomineeSection>

        {nominees.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem 0', color: '#6b7280' }}>
            <p>No hay nominados en esta sala aún.</p>
            <p>¡Sé el primero en crear uno!</p>
          </div>
        ) : (
          <div>
            <h3 style={{ color: '#374151', marginBottom: '1.5rem' }}>
              📋 Nominados Actuales ({nominees.length})
            </h3>
            <NomineesList>
              {nominees.map((nominee) => (
                <NomineeCard key={nominee.id}>
                  <NomineeName>{nominee.name}</NomineeName>
                  {nominee.description && (
                    <NomineeDescription>{nominee.description}</NomineeDescription>
                  )}
                  <NomineeInfo>
                    <span>Creado por: {nominee.creator_name}</span>
                    <span>{new Date(nominee.created_at).toLocaleDateString()}</span>
                  </NomineeInfo>
                  <NomineeStats>
                    <span>📸 {nominee.photo_count} fotos</span>
                    <span>🏷️ {nominee.nickname_count} apodos</span>
                  </NomineeStats>
                  <ActionButtons>
                    <SmallButton onClick={() => navigate(`/upload-photo?room_id=${roomId}&nominee_id=${nominee.id}`)}>
                      Subir Foto
                    </SmallButton>
                    <SmallButton onClick={() => navigate(`/nickname?room_id=${roomId}&nominee_id=${nominee.id}`)}>
                      Agregar Apodo
                    </SmallButton>
                    <DeleteButton onClick={() => handleDeleteNominee(nominee.id)}>
                      Eliminar
                    </DeleteButton>
                  </ActionButtons>
                </NomineeCard>
              ))}
            </NomineesList>
          </div>
        )}
      </Card>
    </Container>
  );
};

export default NomineeManagement;